import { RefreshCw, CircleCheck, CircleAlert, TriangleAlert, Printer as PrinterIcon } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';

import { formatDuration } from '@features/printer/utils/print-format';
import { usePrinterLog } from '@features/printer/hooks/use-printer-log';
import { usePrinter } from '@features/printer/providers/printer-provider';
import { type PrinterRow, buildPrinterOverview } from '@features/printer/utils/printer-overview';

const DOT: Record<string, string> = {
  ok: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-destructive',
  unknown: 'bg-muted-foreground/40',
};

const TYPE_LABELS: Record<string, string> = { BARCODE: 'Étiquettes code-barre', A4: 'Documents A4' };

const Dot = ({ level }: { level: string }) => (
  <span aria-hidden className={cn('size-2 shrink-0 rounded-full', DOT[level])} />
);

const Line = ({ label, value, level }: { label: string; value: string; level: string }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="flex items-center gap-2 font-medium">
      <Dot level={level} />
      {value}
    </span>
  </div>
);

const rowStatus = (row: PrinterRow): string => {
  if (row.installed === null) {
    return 'Indisponible';
  }
  if (row.installed === false) {
    return 'Introuvable dans QZ Tray';
  }
  return row.statusText ?? 'Prête';
};

const SECTION = 'text-xs font-semibold tracking-wide text-muted-foreground uppercase';

/** Header button + popover: QZ Tray / computer / IP state, each printer with its status, and the latest print activity. */
export const PrinterStatusPanel = () => {
  const { isQzReady, isComputerExist, isIPv4, ip, printerList, qzPrinters, printerStatuses, reconnectQz } =
    usePrinter();
  const { logs } = usePrinterLog();

  const overview = buildPrinterOverview({
    isQzReady,
    isComputerExist,
    isIPv4,
    printerList,
    qzPrinters,
    printerStatuses,
  });

  const unassociated = qzPrinters.filter((item) => !printerList.some((printer) => printer.originalName === item.name));
  const jobs = logs.filter((log) => log.status);
  const recent = [...jobs].reverse().slice(0, 4);
  const sent = jobs.filter((log) => log.status === 'sent' && log.durationMs !== undefined);
  const failed = jobs.filter((log) => log.status === 'failed').length;
  const average = sent.length
    ? Math.round(sent.reduce((sum, log) => sum + (log.durationMs ?? 0), 0) / sent.length)
    : undefined;

  const summary =
    overview.level === 'ok' ? 'Prêt à imprimer' : overview.level === 'warning' ? 'Attention' : 'Impression impossible';
  const SummaryIcon =
    overview.level === 'ok' ? CircleCheck : overview.level === 'warning' ? TriangleAlert : CircleAlert;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label={`Imprimantes : ${summary}`}
          className="relative text-muted-foreground"
          size="icon-sm"
          variant="ghost"
        >
          <PrinterIcon />
          <span
            aria-hidden
            className={cn('absolute top-1 right-1 size-2 rounded-full ring-2 ring-canvas', DOT[overview.level])}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <SummaryIcon
            className={cn(
              'size-5',
              overview.level === 'ok' && 'text-success',
              overview.level === 'warning' && 'text-warning',
              overview.level === 'error' && 'text-destructive'
            )}
          />
          <div className="min-w-0 grow">
            <p className="text-sm font-semibold">{summary}</p>
            <p className="line-clamp-2 text-xs text-muted-foreground">
              {overview.issues.length > 0 ? overview.issues.join(' · ') : 'QZ Tray, ordinateur et imprimantes OK'}
            </p>
          </div>
          <Button onClick={() => void reconnectQz()} size="sm" variant="outline">
            <RefreshCw />
            Actualiser
          </Button>
        </div>

        <div className="flex flex-col gap-1 px-4 py-3">
          <p className={SECTION}>Poste</p>
          <Line label="QZ Tray" level={isQzReady ? 'ok' : 'error'} value={isQzReady ? 'Connecté' : 'Inaccessible'} />
          <Line
            label="Ordinateur"
            level={isComputerExist ? 'ok' : 'warning'}
            value={isComputerExist ? 'Configuré' : 'Non configuré'}
          />
          <Line label="IP locale" level={isIPv4 ? 'ok' : 'warning'} value={isIPv4 ? ip : 'Inaccessible'} />
        </div>

        <div className="flex flex-col gap-2 border-t border-border px-4 py-3">
          <p className={SECTION}>Imprimantes</p>
          {overview.rows.length === 0 && (
            <p className="text-sm text-muted-foreground">Aucune imprimante configurée pour ce poste.</p>
          )}
          {overview.rows.map((row) => (
            <div className="flex items-start gap-3" key={row.printer.originalName}>
              <span className="mt-1.5">
                <Dot level={row.installed === null ? 'unknown' : row.level === 'unknown' ? 'ok' : row.level} />
              </span>
              <div className="min-w-0 grow">
                <p className="truncate text-sm font-medium">{row.printer.name}</p>
                <p className="truncate font-mono text-xs text-muted-foreground">{row.printer.originalName}</p>
                <p className="text-xs text-muted-foreground">
                  {TYPE_LABELS[row.printer.type] ?? row.printer.type}
                  {row.isDefault ? ' · par défaut' : ''}
                </p>
              </div>
              <Badge
                size="sm"
                variant={
                  row.installed === false || row.level === 'error'
                    ? 'destructive'
                    : row.level === 'warning'
                      ? 'warning'
                      : row.installed === null
                        ? 'secondary'
                        : 'success'
                }
              >
                {rowStatus(row)}
              </Badge>
            </div>
          ))}
        </div>

        {unassociated.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-border px-4 py-3">
            <p className={SECTION}>Détectées par QZ Tray, non associées</p>
            {unassociated.map((item) => (
              <div className="flex items-center gap-3 text-sm" key={item.name}>
                <Dot level="unknown" />
                <span className="min-w-0 grow truncate font-mono text-xs">{item.name}</span>
                {item.default && (
                  <Badge size="sm" variant="secondary">
                    Par défaut
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2 border-t border-border px-4 py-3">
          <p className={SECTION}>Activité de la session</p>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune impression pour le moment.</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {jobs.length} impression{jobs.length > 1 ? 's' : ''}
                {failed > 0 && <span className="font-medium text-destructive"> · {failed} en échec</span>}
                {average !== undefined && <> · envoi moyen {formatDuration(average)}</>}
              </p>
              <ul className="flex flex-col gap-1">
                {recent.map((log, index) => (
                  <li className="flex items-center gap-2 text-sm" key={log.id ?? index}>
                    <Dot
                      level={
                        log.status === 'sent'
                          ? 'ok'
                          : log.status === 'pending'
                            ? 'unknown'
                            : log.status === 'failed'
                              ? 'error'
                              : 'warning'
                      }
                    />
                    <span className="min-w-0 grow truncate">
                      {log.type} · {log.name}
                    </span>
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">
                      {formatDuration(log.durationMs)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
